import { createColumnHelper } from '@tanstack/react-table';

import { InventoryCount } from 'types/inventory';
import { Site } from 'types/site';
import { convertUTCToLocal } from 'utils/dateUtils';
import { InventoryReportStatusMap } from 'constants/itemtypes';

const col = createColumnHelper<InventoryCount>();

/**
 * The 9 data columns shared verbatim by InventoryTabContent and
 * ArchivedInventory — the two CustomTable versions differed only in flex
 * ratios and one noOfLines={2} that had no visible effect, never in the
 * underlying cell logic. ArchivedInventory has no actions column, so that
 * column is added separately by InventoryTabContent on top of this list.
 */
export const buildInventoryCountColumns = (
  sites: Site[] | undefined,
  itemCount: (count: InventoryCount) => number,
  caseCount: (count: InventoryCount) => number,
) => [
  col.accessor('countNumber', {
    id: 'countNumber',
    header: 'Count No.',
    minSize: 90,
    meta: { align: 'center' },
  }),
  col.accessor((count) => convertUTCToLocal(count?.countDate, true, true), {
    id: 'countDate',
    header: 'Count Date',
    minSize: 150,
    meta: { align: 'center', sortField: 'countDate' },
  }),
  col.accessor(
    (count) => sites?.find((_site) => _site._id === count?.site)?.name || '-',
    {
      id: 'site',
      header: 'Site',
      minSize: 160,
      meta: { align: 'center', sortField: 'site.name' },
    },
  ),
  col.accessor((count) => itemCount(count), {
    id: 'itemCount',
    header: 'Item Count',
    minSize: 100,
    meta: { align: 'center' },
  }),
  col.accessor((count) => caseCount(count), {
    id: 'caseCount',
    header: 'Case Count',
    minSize: 100,
    meta: { align: 'center' },
  }),
  col.accessor(
    (count) =>
      typeof count?.pendingPickup === 'number' ? count?.pendingPickup : '-',
    {
      id: 'pendingPickup',
      header: 'Pending Counts',
      minSize: 120,
      meta: { align: 'center' },
    },
  ),
  col.accessor((count) => count?.harvestCount || 0, {
    id: 'harvestCount',
    header: 'Harvest Summary Count',
    minSize: 150,
    meta: { align: 'center' },
  }),
  col.accessor((count) => count?.notes || '', {
    id: 'notes',
    header: 'Notes',
    minSize: 200,
    size: 320,
    meta: { align: 'center' },
  }),
  col.accessor((count) => InventoryReportStatusMap[count?.status], {
    id: 'status',
    header: 'Status',
    minSize: 110,
    meta: { align: 'center' },
  }),
];
